/**
 * useWebSocket — Core WebSocket hook for real-time data push.
 *
 * Manages a single WebSocket connection per browser tab to the backend
 * /ws/live endpoint. Handles:
 *   - Auto-reconnection with exponential backoff
 *   - Channel subscription/unsubscription
 *   - Heartbeat ping every 30s
 *   - Connection state tracking
 */

import { useEffect, useRef, useState, useCallback } from 'react'

export interface WebSocketMessage {
    channel: string
    data: unknown
    ts: number
}

interface UseWebSocketOptions {
    /** Channels to auto-subscribe to on connect */
    channels?: string[]
    /** Callback for incoming messages */
    onMessage?: (msg: WebSocketMessage) => void
    /** Whether the WebSocket should be active (default: true) */
    enabled?: boolean
}

interface UseWebSocketReturn {
    isConnected: boolean
    lastMessage: WebSocketMessage | null
    /** Send a raw JSON message */
    send: (msg: Record<string, unknown>) => void
    /** Subscribe to a channel */
    subscribe: (channel: string) => void
    /** Unsubscribe from a channel */
    unsubscribe: (channel: string) => void
}

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000
const HEARTBEAT_INTERVAL_MS = 30000

function getWsUrl(): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // In development (Vite dev server), the API is proxied via vite.config
    // In production, the backend serves at the same host
    return `${proto}//${window.location.host}/ws/live`
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
    const { channels = [], onMessage, enabled = true } = options
    const [isConnected, setIsConnected] = useState(false)
    const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)

    const wsRef = useRef<WebSocket | null>(null)
    const reconnectAttempt = useRef(0)
    const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)
    const channelsRef = useRef<Set<string>>(new Set(channels))
    const onMessageRef = useRef(onMessage)

    // Keep callback ref fresh without triggering reconnects
    onMessageRef.current = onMessage

    // Track desired channels
    useEffect(() => {
        channelsRef.current = new Set(channels)
    }, [channels.join(',')])

    const cleanup = useCallback(() => {
        if (heartbeatTimer.current) {
            clearInterval(heartbeatTimer.current)
            heartbeatTimer.current = null
        }
        if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current)
            reconnectTimer.current = null
        }
    }, [])

    const sendRaw = useCallback((msg: Record<string, unknown>) => {
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(msg))
        }
    }, [])

    const subscribe = useCallback((channel: string) => {
        channelsRef.current.add(channel)
        sendRaw({ action: 'subscribe', channel })
    }, [sendRaw])

    const unsubscribe = useCallback((channel: string) => {
        channelsRef.current.delete(channel)
        sendRaw({ action: 'unsubscribe', channel })
    }, [sendRaw])

    const connect = useCallback(() => {
        if (!enabled) return
        if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return

        const url = getWsUrl()
        const ws = new WebSocket(url)
        wsRef.current = ws

        ws.onopen = () => {
            setIsConnected(true)
            reconnectAttempt.current = 0

            // Subscribe to all desired channels
            for (const ch of channelsRef.current) {
                ws.send(JSON.stringify({ action: 'subscribe', channel: ch }))
            }

            // Start heartbeat
            heartbeatTimer.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ action: 'ping' }))
                }
            }, HEARTBEAT_INTERVAL_MS)
        }

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data) as WebSocketMessage
                // Skip system messages for lastMessage state
                if (msg.channel !== '__system') {
                    setLastMessage(msg)
                }
                onMessageRef.current?.(msg)
            } catch {
                // Ignore malformed messages
            }
        }

        ws.onclose = () => {
            setIsConnected(false)
            cleanup()

            if (!enabled) return

            // Exponential backoff reconnect
            const delay = Math.min(
                RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt.current),
                RECONNECT_MAX_MS
            )
            reconnectAttempt.current++
            reconnectTimer.current = setTimeout(connect, delay)
        }

        ws.onerror = () => {
            // onclose will fire after onerror, so reconnect is handled there
        }
    }, [enabled, cleanup])

    // Connect/disconnect based on enabled flag
    useEffect(() => {
        if (enabled) {
            connect()
        } else {
            cleanup()
            if (wsRef.current) {
                wsRef.current.close()
                wsRef.current = null
            }
            setIsConnected(false)
        }

        return () => {
            cleanup()
            if (wsRef.current) {
                wsRef.current.close()
                wsRef.current = null
            }
        }
    }, [enabled, connect, cleanup])

    return {
        isConnected,
        lastMessage,
        send: sendRaw,
        subscribe,
        unsubscribe,
    }
}
